import React, { useCallback, useEffect, useState } from "react";
import { defaultPageable, emptyPage, Pageable, PagedResult } from "../../technical/rest/pageable";
import { useAxios } from "../../technical/rest/AxiosProvider";
import { AxiosInstance } from "axios";
import { Grid, StyledEngineProvider, ThemeProvider, useTheme } from "@mui/material";
import { GridSize, GridSpacing } from "@mui/material/Grid/Grid";
import MUIDataTable, {
  debounceSearchRender,
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableState
} from "mui-datatables";
import { muiDataTablesLabels } from "./muiDataTableTranslation";
import { useTranslation } from "react-i18next";
import { muiDataTableTheme } from "./muiDataTableTheme";
import { contentPageStyle } from "./ContentPageTable";
import { ResponsiveStyleValue } from "@mui/system";

export type PagedQuery<ENTITY> = (axios: AxiosInstance, pageable: Pageable, params?: string) => Promise<PagedResult<ENTITY>>;
export type TableOptions =
  Partial<Omit<MUIDataTableOptions,
    'onRowClick' | 'onChangePage' | 'onChangeRowsPerPage' | 'onColumnSortChange' | 'onFilterChange' | 'onSearchChange' | 'onViewColumnsChange'>>;

export function PaginatedContentPageTable<ENTITY>(props: {
  pagedQuery: PagedQuery<ENTITY>,
  columns: MUIDataTableColumn[],
  handleRowClick?: (entity: ENTITY, dataIndex: number) => void,
  dataVersion: number, // To be used to signal data changes and force re-render
  tableOptions?: TableOptions,
  onViewColumnsChange?: (columns: MUIDataTableColumnDef[]) => void,
  onParamsChange?: (params: string) => void,
  onTotalCountChange?: (count: number) => void,
  additionalQueryParams?: string,
  tableGridSize?: ResponsiveStyleValue<GridSpacing>,
}) {
  const {pagedQuery, columns, handleRowClick, dataVersion, tableOptions, onViewColumnsChange, onParamsChange, onTotalCountChange, additionalQueryParams} = props;
  const axios = useAxios();
  const {t} = useTranslation();
  const theme = useTheme();
  const [pagedResult, setPagedResult] = useState<PagedResult<ENTITY>>(emptyPage());
  const [pageable, setPageable] = useState<Pageable>(defaultPageable());  // Size = 10
  const [filterList, setFilterList] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>();
  const [params, setParams] = useState<string>(filterList.join('&'));
  const [tableColumns, setTableColumns] = useState<MUIDataTableColumn[]>(props.columns);
  const [tableGridSize, setTableGridSize] = useState(props.tableGridSize)

  useEffect(() => {
    pagedQuery(axios, pageable, params).then(result => {
      setPagedResult(result);
      onTotalCountChange?.(result.page.totalElements);
    });
    if (dataVersion) {
      // just use it here
    }
  }, [axios, pageable, params, pagedQuery, dataVersion, onTotalCountChange])

  useEffect(() => {
    onParamsChange?.(params);
  }, [params, onParamsChange]);

  useEffect(() => {
    setTableColumns(columns);
  }, [columns])

  useEffect(() => {
    // console.info('PaginatedContentPageTable: Columns changed...')
    const viewColumns = tableColumns.filter((tableColumn) => tableColumn.options?.display);
    // console.info('ViewColumns: ' + viewColumns.length);
    onViewColumnsChange && onViewColumnsChange(viewColumns);
  }, [tableColumns, onViewColumnsChange])

  const handleTableRowClick = useCallback((rowData: string[], rowMeta: {dataIndex: number; rowIndex: number}) => {
    handleRowClick && handleRowClick(pagedResult.content[rowMeta.rowIndex], rowMeta.dataIndex);
  }, [handleRowClick, pagedResult]);

  const handleViewColumnsChange = useCallback((changedColumn: string, action: string) => {
    if (columns.length > props.columns.filter(column => column.options?.display).length) {
      console.info('Columns are bigger')
      setTableGridSize({lg: 12 as GridSize, xl: 12 as GridSize})
    } else {
      setTableGridSize({lg: 8 as GridSize, xl: 6 as GridSize})
    }
    setTableColumns(cols =>
      cols.map((column: MUIDataTableColumn) => {
        if (column.name === changedColumn) {
          return {...column, options: {...column.options, display: action === "add"}}
        } else {
          return column
        }
      }))
  }, [props.columns, columns.length]);

  function mapFilterEntry(entry: string [], index: number) {
    if (!entry || entry.length === 0) {
      return
    }
    const name = (tableColumns[index] as any).name as string;
    return entry.map(appName => name + '=' + appName).join('&');
  }

  useEffect(() => {
    const parts: string[] = [];
    if (additionalQueryParams) parts.push(additionalQueryParams);
    parts.push(...filterList);
    if (searchText) parts.push('search=' + searchText);
    setParams(parts.filter(Boolean).join('&'));
  }, [searchText, filterList, additionalQueryParams]);

  return (
    <Grid container={true} justifyContent="center" sx={contentPageStyle(theme).tableContainer}>
      <Grid item={true} xs={12} {...tableGridSize} sx={contentPageStyle(theme).tableContainerItem}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={muiDataTableTheme}>
            <MUIDataTable data={pagedResult.content as Array<any> as Array<object>}
                          columns={tableColumns}
                          title={''}
                          options={{
                            enableNestedDataAccess: ".",
                            elevation: 2,
                            pagination: true,
                            rowsPerPage: pageable.size,
                            count: pagedResult.page.totalElements,
                            rowsPerPageOptions: [10, 25, 100],
                            selectableRows: 'none',
                            serverSide: true,
                            search: false,
                            filter: false,
                            download: false,
                            print: false,
                            viewColumns: false,
                            responsive: 'standard',
                            customSearchRender: debounceSearchRender(300),
                            onRowClick: handleRowClick ? handleTableRowClick : undefined,
                            rowHover: !!handleTableRowClick,
                            onChangePage: (page) => setPageable(curr => ({...curr, page: page})),
                            onChangeRowsPerPage: (rowsPerPage) => setPageable(curr => ({...curr, page: 0, size: rowsPerPage})),
                            onColumnSortChange: (changedColumn: string, direction: 'asc' | 'desc') => setPageable(curr => ({
                              ...curr,
                              sort: {field: changedColumn, order: direction}
                            })),
                            onFilterChange: (changedColumn: string | MUIDataTableColumn | null,
                                             filterList: MUIDataTableState['filterList']) =>
                              setFilterList(filterList.map(mapFilterEntry).filter((entry) => !!entry) as string[]),
                            onSearchChange: (newSearchText: string | null) => setSearchText(newSearchText || undefined),
                            onViewColumnsChange: handleViewColumnsChange,
                            textLabels: muiDataTablesLabels(t),
                            ...tableOptions
                          }}
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Grid>
    </Grid>
  );
}
